import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './CandidateResult.css';

const EXAM_API = process.env.REACT_APP_EXAM_API_URL || `${process.env.REACT_APP_API_URL || '/api'}/exam`;

/** Hint tooltip — hover to see explanation */
const Hint = ({ text }) => (
  <span className="eval-hint" aria-label={text} data-tooltip={text}>
    ⓘ
  </span>
);

export default function CandidateResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [expandedParams, setExpandedParams] = useState({});
  const [expandedGrammar, setExpandedGrammar] = useState(false);
  const [expandedSpelling, setExpandedSpelling] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState({});

  useEffect(() => {
    if (email) fetchResult();
  }, [email]);

  const fetchResult = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${EXAM_API}/evaluation/candidate/${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          setResult(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch result:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const toggleParam = (sessionIdx, paramIdx) => {
    const key = `${sessionIdx}-${paramIdx}`;
    setExpandedParams((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isParamExpanded = (sessionIdx, paramIdx) => {
    return !!expandedParams[`${sessionIdx}-${paramIdx}`];
  };

  const toggleSession = (idx) => {
    setExpandedSessions((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const getGradeBadgeClass = (grade) => {
    if (!grade) return 'cat-grade-badge';
    const g = grade.toLowerCase();
    if (g === 'excellent') return 'cat-grade-badge cat-grade-excellent';
    if (g === 'very good') return 'cat-grade-badge cat-grade-very-good';
    if (g === 'good') return 'cat-grade-badge cat-grade-good';
    if (g === 'average') return 'cat-grade-badge cat-grade-average';
    if (g === 'bad') return 'cat-grade-badge cat-grade-bad';
    if (g === 'poor') return 'cat-grade-badge cat-grade-poor';
    return 'cat-grade-badge';
  };

  /** Determine if the evaluation uses new CAT Sheet format */
  const isCATSheetFormat = (evaluation) => {
    return evaluation && Array.isArray(evaluation.parameters);
  };

  /** Render OLD evaluation format (scores array with 0-10 scores) */
  const renderOldEvaluation = (evaluation) => (
    <div className="result-llm-scores">
      <h4>AI Evaluation — Overall: {evaluation.overall_score}/10</h4>
      <div className="result-scores-grid">
        {evaluation.scores?.map((score, scIdx) => (
          <div key={scIdx} className="result-score-card">
            <div className="result-score-header">
              <span>{score.criterion}</span>
              <span className="result-score-num">{score.score}/10</span>
            </div>
            <p className="result-score-just">{score.justification}</p>
          </div>
        ))}
      </div>
    </div>
  );

  /** Render NEW CAT Sheet evaluation format */
  const renderCATSheetEvaluation = (evaluation, sessionIdx, { hideVerdict = false } = {}) => (
    <div className="cat-evaluation">
      {/* Verdict Banner */}
      {!hideVerdict && (
        <div className={`cat-verdict-banner ${evaluation.verdict === 'PASS' ? 'cat-verdict-pass' : 'cat-verdict-fail'}`}>
          <div className="cat-verdict-main">
            <span className="cat-verdict-label">{evaluation.verdict}</span>
            <span className="cat-verdict-score">{evaluation.score_percentage?.toFixed(1)}%</span>
          </div>
          {evaluation.verdict_reasoning && (
            <p className="cat-verdict-reasoning">{evaluation.verdict_reasoning}</p>
          )}
        </div>
      )}

      {/* Summary Row */}
      <div className="cat-summary-row">
        <div className="cat-summary-item">
          <span className="cat-summary-label">Total Score <Hint text="Number of parameters MET out of 6 total parameters evaluated" /></span>
          <span className="cat-summary-value">{evaluation.total_score}/6</span>
        </div>
        <div className="cat-summary-item">
          <span className="cat-summary-label">Score % <Hint text="Percentage of parameters MET: (Total Score / 6) × 100. Must be ≥60% to pass" /></span>
          <span className="cat-summary-value">{evaluation.score_percentage?.toFixed(1)}%</span>
        </div>
        <div className="cat-summary-item">
          <span className="cat-summary-label">Grammar <Hint text="Grade based on grammar errors found. Must be Good or above to pass" /></span>
          <span className={getGradeBadgeClass(evaluation.grammar?.grade)}>
            {evaluation.grammar?.grade || '—'}
          </span>
        </div>
        <div className="cat-summary-item">
          <span className="cat-summary-label">Spelling <Hint text="Grade based on spelling errors found. Must be Good or above to pass" /></span>
          <span className={getGradeBadgeClass(evaluation.spelling?.grade)}>
            {evaluation.spelling?.grade || '—'}
          </span>
        </div>
      </div>

      {/* Parameter Cards */}
      <div className="cat-parameters-section">
        <h4>Parameters <Hint text="6 communication parameters evaluated. Each parameter has descriptors that must meet a threshold to be marked MET" /></h4>
        {evaluation.parameters?.map((param, pIdx) => (
          <div
            key={pIdx}
            className={`cat-parameter-card ${param.status === 'MET' ? 'cat-param-met' : 'cat-param-not-met'}`}
          >
            <div
              className="cat-param-header"
              onClick={() => toggleParam(sessionIdx, pIdx)}
              role="button"
              tabIndex={0}
              aria-expanded={isParamExpanded(sessionIdx, pIdx)}
              aria-label={`Toggle descriptors for ${param.name}`}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleParam(sessionIdx, pIdx); } }}
            >
              <div className="cat-param-title-row">
                <span className="cat-param-name">{param.name}</span>
                <span className={param.status === 'MET' ? 'cat-param-badge-met' : 'cat-param-badge-not-met'}>
                  {param.status}
                </span>
              </div>
              <span className="cat-threshold-label">Requires: {param.threshold_rule}</span>
              {param.parse_error && (
                <span className="cat-parse-error-indicator" title={param.parse_error}>⚠️ Parse Error</span>
              )}
              <span className="cat-expand-icon">{isParamExpanded(sessionIdx, pIdx) ? '▾' : '▸'}</span>
            </div>

            {isParamExpanded(sessionIdx, pIdx) && (
              <div className="cat-descriptor-list" role="list" aria-label={`Descriptors for ${param.name}`}>
                {param.descriptors?.map((desc, dIdx) => (
                  <div key={dIdx} className="cat-descriptor-item" role="listitem">
                    <div className="cat-descriptor-status-row">
                      <span className={`cat-descriptor-dot ${desc.status === 'MET' ? 'cat-dot-met' : 'cat-dot-not-met'}`}></span>
                      <span className="cat-descriptor-name">{desc.name}</span>
                      {desc.description && (
                        <span className="cat-descriptor-criteria">{desc.description}</span>
                      )}
                      <span className={desc.status === 'MET' ? 'cat-param-badge-met cat-badge-sm' : 'cat-param-badge-not-met cat-badge-sm'}>
                        {desc.status}
                      </span>
                    </div>
                    {desc.justification && (
                      <p className="cat-descriptor-justification">{desc.justification}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Grammar Section */}
      {evaluation.grammar && (
        <div className="cat-grade-section">
          <div
            className="cat-grade-header"
            onClick={() => setExpandedGrammar((prev) => !prev)}
            role="button"
            tabIndex={0}
            aria-expanded={expandedGrammar}
            aria-label="Toggle grammar errors"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedGrammar((prev) => !prev); } }}
          >
            <span className="cat-grade-title">Grammar</span>
            <span className={getGradeBadgeClass(evaluation.grammar.grade)}>{evaluation.grammar.grade}</span>
            <span className="cat-grade-error-count">{evaluation.grammar.error_count} errors</span>
            {evaluation.grammar.parse_error && (
              <span className="cat-parse-error-indicator" title={evaluation.grammar.parse_error}>⚠️</span>
            )}
            <span className="cat-expand-icon">{expandedGrammar ? '▾' : '▸'}</span>
          </div>
          {expandedGrammar && evaluation.grammar.errors_listed?.length > 0 && (
            <ol className="cat-error-list" aria-label="Grammar errors">
              {evaluation.grammar.errors_listed.map((err, eIdx) => (
                <li key={eIdx}>{err}</li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Spelling Section */}
      {evaluation.spelling && (
        <div className="cat-grade-section">
          <div
            className="cat-grade-header"
            onClick={() => setExpandedSpelling((prev) => !prev)}
            role="button"
            tabIndex={0}
            aria-expanded={expandedSpelling}
            aria-label="Toggle spelling errors"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedSpelling((prev) => !prev); } }}
          >
            <span className="cat-grade-title">Spelling</span>
            <span className={getGradeBadgeClass(evaluation.spelling.grade)}>{evaluation.spelling.grade}</span>
            <span className="cat-grade-error-count">{evaluation.spelling.error_count} errors</span>
            {evaluation.spelling.parse_error && (
              <span className="cat-parse-error-indicator" title={evaluation.spelling.parse_error}>⚠️</span>
            )}
            <span className="cat-expand-icon">{expandedSpelling ? '▾' : '▸'}</span>
          </div>
          {expandedSpelling && evaluation.spelling.errors_listed?.length > 0 && (
            <ol className="cat-error-list" aria-label="Spelling errors">
              {evaluation.spelling.errors_listed.map((err, eIdx) => (
                <li key={eIdx}>{err}</li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="result-page">
        <p style={{ color: 'var(--text-tertiary)', padding: '40px', textAlign: 'center' }}>Loading...</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="result-page">
        <div className="result-header">
          <h2>Candidate Result</h2>
          <button className="btn-back-result" onClick={() => navigate('/admin/evaluation')}>← Back</button>
        </div>
        <p style={{ color: 'var(--text-tertiary)', padding: '20px' }}>No evaluation data found for {email}</p>
      </div>
    );
  }

  return (
    <div className="result-page">
      <div className="result-header">
        <h2>Candidate Result</h2>
        <button className="btn-back-result" onClick={() => navigate('/admin/evaluation')}>← Back</button>
      </div>

      {/* Candidate info */}
      <div className="result-candidate-info">
        <div className="result-info-item">
          <span className="result-info-label">Candidate</span>
          <span className="result-info-value">{result.candidate_name}</span>
        </div>
        <div className="result-info-item">
          <span className="result-info-label">Email</span>
          <span className="result-info-value">{result.email}</span>
        </div>
        <div className="result-info-item">
          <span className="result-info-label">Process</span>
          <span className="result-info-value">{result.process}</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="result-summary-grid">
        <div className="result-summary-card">
          <span className="result-summary-label">Sessions Completed <Hint text="Total number of scenario sessions the candidate has finished" /></span>
          <span className="result-summary-value">{result.sessions_completed}</span>
        </div>
        <div className="result-summary-card">
          <span className="result-summary-label">Total Interactions <Hint text="Sum of all interactions across all sessions" /></span>
          <span className="result-summary-value">{result.total_questions_answered}</span>
        </div>
        <div className="result-summary-card">
          <span className="result-summary-label">Total Time <Hint text="Combined time spent by the candidate across all sessions" /></span>
          <span className="result-summary-value">{formatTime(result.total_time_seconds)}</span>
        </div>
      </div>

      {/* Scenario Tiles */}
      <div className="result-scenario-tiles">
        {result.sessions.map((session, idx) => (
          <div className="scenario-tile" key={idx}>
            <h4>Scenario {idx + 1}</h4>
            <p>{session.scenario_title}</p>
            <div className="scenario-tile-meta">
              <span>{session.questions_answered} interactions</span>
              <span>{formatTime(session.total_time_seconds)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Sessions Table */}
      <div className="result-table-wrapper">
        <table className="result-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Scenario</th>
              <th>Interactions</th>
              <th>Time</th>
              <th>Status</th>
              <th>Completed</th>
            </tr>
          </thead>
          <tbody>
            {result.sessions.map((s, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>{s.scenario_title}</td>
                <td>{s.questions_answered}</td>
                <td>{formatTime(s.total_time_seconds)}</td>
                <td><span className="score-badge">{s.status}</span></td>
                <td>{s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detailed Session Results */}
      {result.sessions.map((session, sIdx) => (
        <div key={sIdx} className="result-session-detail">
          <div
            className="result-session-header"
            onClick={() => toggleSession(sIdx)}
            role="button"
            tabIndex={0}
            aria-expanded={!!expandedSessions[sIdx]}
            aria-label={`Toggle details for Session ${sIdx + 1}`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSession(sIdx); } }}
          >
            <span className="result-session-toggle">{expandedSessions[sIdx] ? '−' : '+'}</span>
            <h3>Session {sIdx + 1} — {session.scenario_title || 'Direct Q&A'}</h3>
          </div>

          {/* Verdict Preview — always visible */}
          {session.llm_evaluation && isCATSheetFormat(session.llm_evaluation) && (
            <div className="cat-verdict-banner-preview">
              <div className={`cat-verdict-banner ${session.llm_evaluation.verdict === 'PASS' ? 'cat-verdict-pass' : 'cat-verdict-fail'}`}>
                <div className="cat-verdict-main">
                  <span className="cat-verdict-label">{session.llm_evaluation.verdict}</span>
                  <span className="cat-verdict-score">{session.llm_evaluation.score_percentage?.toFixed(1)}%</span>
                </div>
                {session.llm_evaluation.verdict_reasoning && (
                  <p className="cat-verdict-reasoning">{session.llm_evaluation.verdict_reasoning}</p>
                )}
              </div>
            </div>
          )}

          {session.llm_evaluation && !isCATSheetFormat(session.llm_evaluation) && session.llm_evaluation.scores && (
            <div className="cat-verdict-banner-preview">
              <div className="result-llm-preview">
                <span>AI Evaluation — Overall: {session.llm_evaluation.overall_score}/10</span>
                {session.llm_evaluation.verdict && (
                  <span className="score-badge">{session.llm_evaluation.verdict}</span>
                )}
              </div>
            </div>
          )}

          {expandedSessions[sIdx] && (
            <div className="result-session-body">
              {/* LLM Evaluation details (skip verdict banner since it's already shown) */}
              {session.llm_evaluation && (
                isCATSheetFormat(session.llm_evaluation)
                  ? renderCATSheetEvaluation(session.llm_evaluation, sIdx, { hideVerdict: true })
                  : session.llm_evaluation.scores
                    ? renderOldEvaluation(session.llm_evaluation)
                    : null
              )}

              {/* Q&A Detail */}
              <div className="result-qa-section">
                <h4>Conversation ({session.questions_answered} interactions)</h4>
                {session.questions?.map((q, qIdx) => (
                  <div key={qIdx} className="result-qa-item">
                    <div className="result-qa-header">
                      <span className="result-qa-num">Interaction {qIdx + 1}</span>
                      <span className="result-qa-time">⏱ {formatTime(q.time_taken_seconds)}</span>
                    </div>
                    <div className="result-qa-agent">
                      <strong>Candidate:</strong> {q.answer}
                    </div>
                    <div className="result-qa-customer">
                      <strong>Customer:</strong> {q.question_text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
